import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Ambient river-flow soundbed for the landing page.
 *
 * Default INTENT is ON, but browsers block audio until a user gesture.
 * Strategy:
 *   1. Boot in `enabled = true` (unless localStorage has "0" from a previous
 *      explicit mute — we always respect that).
 *   2. Try `play()` on mount. If the browser allows it (rare but possible
 *      on returns within the engagement window), great — we're already
 *      sounding.
 *   3. If `play()` rejects with NotAllowedError, install one-shot listeners
 *      for `pointerdown / touchstart / keydown / scroll`. The very first
 *      time the visitor does any of those, we retry `play()`. That first
 *      gesture is almost always within 1–2 seconds of arrival (a scroll
 *      on the home page) so the river kicks in without ceremony.
 *   4. The toggle still works at any moment to mute. Explicit mute writes
 *      "0" to localStorage so future visits respect the preference.
 *
 * Volume locked at 0.3 (moderate — sits under speech in another tab).
 * Loops indefinitely. Pauses on tab hide via Page Visibility API.
 */
const STORAGE_KEY = "ocoee.ambient.enabled";
const SRC = "/audio/ambient-river.mp3";
const VOLUME = 0.3;

export function AmbientAudio() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Default ON unless the user has explicitly muted on a previous visit.
  // We compute the initial state synchronously so React doesn't paint a
  // brief OFF state before the localStorage useEffect catches up.
  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return true; // SSR fallback
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    return stored !== "0"; // null (first visit) → true; "1" → true; "0" → false
  });
  // Round 3 Agent C (item #16): hide the sound toggle while the soft
  // keyboard is up on phones — same heuristic the dock + back-to-top use.
  // Detected via window.visualViewport; falls back to "always visible" on
  // browsers that don't expose it (older Safari, some embedded WebViews).
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    const check = () => {
      const layoutH = window.innerHeight;
      const visualH = vv.height;
      if (!layoutH) return;
      setKeyboardOpen((layoutH - visualH) / layoutH > 0.2);
    };
    check();
    vv.addEventListener("resize", check);
    vv.addEventListener("scroll", check);
    return () => {
      vv.removeEventListener("resize", check);
      vv.removeEventListener("scroll", check);
    };
  }, []);

  // Apply the enabled state to the actual <audio> element. If `play()` is
  // blocked by the browser's autoplay policy, install one-shot interaction
  // listeners that retry the moment the user gestures.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = VOLUME;

    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
    }

    if (!enabled) {
      a.pause();
      // Drop the file so a muted session never downloads the MP3.
      if (a.getAttribute("src")) {
        a.removeAttribute("src");
        a.load();
      }
      return;
    }

    // Do not set `src` (and therefore do not fetch) until playback is
    // actually started. Cold load + default-on would otherwise pull
    // ambient-river.mp3 on first paint. Wait for a user gesture.
    const attachSrc = () => {
      if (!a.getAttribute("src")) a.src = SRC;
    };

    let cancelled = false;
    let listener: ((e: Event) => void) | null = null;

    const detach = () => {
      if (listener) {
        document.removeEventListener("pointerdown", listener);
        document.removeEventListener("touchstart", listener);
        listener = null;
      }
    };

    listener = () => {
      if (cancelled) return;
      attachSrc();
      a.play().catch(() => {/* still blocked; let the next gesture retry */});
      detach();
    };
    document.addEventListener("pointerdown", listener, { once: true, passive: true });
    document.addEventListener("touchstart", listener, { once: true, passive: true });

    return () => {
      cancelled = true;
      detach();
    };
  }, [enabled]);

  // Pause on tab hide, resume on return — only if the user had it enabled.
  useEffect(() => {
    const onVisibility = () => {
      const a = audioRef.current;
      if (!a) return;
      if (document.hidden) {
        a.pause();
      } else if (enabled && a.getAttribute("src")) {
        a.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled]);

  return (
    <>
      <audio
        ref={audioRef}
        loop
        preload="none"
        // The element itself is invisible — the toggle is the UI surface.
        aria-hidden="true"
      />
      {/* Polish round 2 — item #14: louder visual state.
            ON  → Volume2 icon + "SOUND ON" label (gold accent class .is-on).
            OFF → VolumeX (struck-through speaker) + "SOUND" label.
          aria-pressed mirrors the visible state for assistive tech; title +
          aria-label spell out the action the click will perform. */}
      <button
        type="button"
        className={`ambient-toggle ${enabled ? "is-on" : "is-off"} ${keyboardOpen ? "is-keyboard-open" : ""}`}
        onClick={() => {
          setEnabled((wasOn) => {
            const next = !wasOn;
            if (next) {
              const a = audioRef.current;
              if (a) {
                if (!a.getAttribute("src")) a.src = SRC;
                a.volume = VOLUME;
                a.play().catch(() => {});
              }
            }
            return next;
          });
        }}
        aria-label={
          enabled
            ? "Mute ambient river sound"
            : "Play ambient river sound"
        }
        aria-pressed={enabled}
        title={enabled ? "Sound is ON · click to mute" : "Sound is OFF · click to play"}
      >
        {enabled ? (
          <Volume2 size={16} aria-hidden="true" />
        ) : (
          <VolumeX size={16} aria-hidden="true" />
        )}
        <span className="ambient-toggle-label">
          {enabled ? "SOUND ON" : "SOUND"}
        </span>
      </button>
    </>
  );
}
